import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { T, familyColor } from "../tokens";

// ─── Icons ──────────────────────────────────────────────────────────────────
const ArrowLeft = () => (
  <svg width={22} height={22} viewBox="0 0 24 24" fill="none">
    <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const SearchIcon = () => (
  <svg width={18} height={18} viewBox="0 0 24 24" fill="none">
    <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
    <path d="M20 20l-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);
const TrashIcon = () => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none">
    <path d="M4 7h16M9 7V5a2 2 0 012-2h2a2 2 0 012 2v2M6 7l1 13a2 2 0 002 2h6a2 2 0 002-2l1-13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const PlusIcon = () => (
  <svg width={22} height={22} viewBox="0 0 24 24" fill="none">
    <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
  </svg>
);
const ChevronDown = () => (
  <svg width={14} height={14} viewBox="0 0 24 24" fill="none">
    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const ShieldIcon = () => (
  <svg width={15} height={15} viewBox="0 0 24 24" fill="none">
    <path d="M12 2l7 4v6c0 4.5-3 8-7 9-4-1-7-4.5-7-9V6l7-4z" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const UserIcon = () => (
  <svg width={15} height={15} viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="1.9" />
    <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
  </svg>
);

// ─── Confirm Modal ───────────────────────────────────────────────────────────
function ConfirmModal({ message, onConfirm, onCancel, confirmLabel = "Confirm", danger = false }) {
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 100,
      background: "rgba(0,0,0,0.45)", display: "flex",
      alignItems: "center", justifyContent: "center", padding: 24,
    }}>
      <div style={{
        background: T.card, borderRadius: T.r.lg, border: `1px solid ${T.line}`,
        padding: 24, maxWidth: 360, width: "100%",
        boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
      }}>
        <div style={{ fontFamily: T.font, fontSize: 15, color: T.ink, lineHeight: 1.55, marginBottom: 20 }}>
          {message}
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onCancel} style={{
            flex: 1, height: 46, border: `1px solid ${T.lineStrong}`, background: T.card,
            color: T.ink, borderRadius: T.r.md, fontFamily: T.font, fontSize: 15,
            fontWeight: 600, cursor: "pointer",
          }}>Cancel</button>
          <button onClick={onConfirm} style={{
            flex: 2, height: 46, border: "none",
            background: danger ? T.rust : T.forest, color: "#fff",
            borderRadius: T.r.md, fontFamily: T.font, fontSize: 15,
            fontWeight: 600, cursor: "pointer",
          }}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────
export default function AdminPageComponent() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const userId = user?.userId;

  // tab: "species" | "users"
  const [tab, setTab] = useState("species");

  // ── Species state ──────────────────────────────────────────────────────────
  const [butterflies, setButterflies] = useState([]);
  const [search, setSearch] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [activeFamily, setActiveFamily] = useState("All");
  const [expandedId, setExpandedId] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const PREVIEW_COUNT = 5;
  const [form, setForm] = useState({
    commonName: "", scientificName: "",
    commonFamilyName: "", scientificFamilyName: "",
    commonSubfamilyName: "", scientificSubfamilyName: "",
    description: "", states: "",
  });

  // ── User state ─────────────────────────────────────────────────────────────
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState(null);
  const [busyIds, setBusyIds] = useState(new Set());
  const [confirm, setConfirm] = useState(null); // { message, onConfirm, danger, confirmLabel }

  // ── Init ───────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!user?.userId) { 
      localStorage.clear();
      navigate("/");
    }
    if (user?.admin) {
      fetchButterflies();
      fetchUsers();
    }
  }, []);

  // ── Species API ────────────────────────────────────────────────────────────
  const fetchButterflies = async () => {
    try {
      const res = await fetch("/api/butterflies");
      setButterflies(await res.json());
    } catch (err) {
      alert("Failed to load butterflies");
    }
  };

  const addButterfly = async () => {
    try {
      const res = await fetch(`api/butterflies?adminUserId=${userId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          states: form.states.split(",").map(s => s.trim()).filter(Boolean),
        }),
      });
      if (!res.ok) throw new Error("Not authorized or invalid data");
      setForm({ commonName: "", scientificName: "", commonFamilyName: "", scientificFamilyName: "", commonSubfamilyName: "", scientificSubfamilyName: "", description: "", states: "" });
      setShowAdd(false);
      fetchButterflies();
    } catch (err) {
      alert("Error adding butterfly: " + err.message);
    }
  };

  const deleteButterfly = (id, name) => {
    setConfirm({
      message: `Delete "${name}" from the species library? This cannot be undone.`,
      confirmLabel: "Delete species",
      danger: true,
      onConfirm: async () => {
        setConfirm(null);
        try {
          const res = await fetch(`api/butterflies/${id}?adminUserId=${userId}`, { method: "DELETE" });
          if (!res.ok) throw new Error("Delete failed");
          fetchButterflies();
        } catch (err) {
          alert("Error deleting butterfly: " + err.message);
        }
      },
    });
  };

  // ── User API ───────────────────────────────────────────────────────────────
  const fetchUsers = async () => {
    setUsersLoading(true);
    setUsersError(null);
    try {
      const res = await fetch(`/api/users?adminUserId=${userId}`);
      if (!res.ok) throw new Error(await res.text());
      setUsers(await res.json());
    } catch (err) {
      setUsersError(err.message || "Failed to load users");
    } finally {
      setUsersLoading(false);
    }
  };

  const setBusy = (id, busy) => setBusyIds(prev => {
    const s = new Set(prev);
    busy ? s.add(id) : s.delete(id);
    return s;
  });

  const toggleAdmin = (target) => {
    const next = !target.admin;
    const action = next ? "Grant admin privileges to" : "Revoke admin privileges from";
    setConfirm({
      message: `${action} "${target.username}"?`,
      confirmLabel: next ? "Make admin" : "Revoke admin",
      danger: !next,
      onConfirm: async () => {
        setConfirm(null);
        setBusy(target.id, true);
        try {
          const res = await fetch(`/api/users/${target.id}/admin?adminUserId=${userId}&isAdmin=${next}`, { method: "PUT" });
          if (!res.ok) throw new Error(await res.text());
          setUsers(prev => prev.map(u => u.id === target.id ? { ...u, admin: next } : u));
        } catch (err) {
          alert("Error updating admin status: " + err.message);
        } finally {
          setBusy(target.id, false);
        }
      },
    });
  };

  const deleteUser = (target) => {
    setConfirm({
      message: `Permanently delete user "${target.username}"? This cannot be undone.`,
      confirmLabel: "Delete user",
      danger: true,
      onConfirm: async () => {
        setConfirm(null);
        setBusy(target.id, true);
        try {
          const res = await fetch(`/api/users/${target.id}?adminUserId=${userId}`, { method: "DELETE" });
          if (!res.ok) throw new Error(await res.text());
          setUsers(prev => prev.filter(u => u.id !== target.id));
        } catch (err) {
          alert("Error deleting user: " + err.message);
        } finally {
          setBusy(target.id, false);
        }
      },
    });
  };

  // ── Access guard ───────────────────────────────────────────────────────────
  if (!user?.admin) {
    return (
      <div className="lep-page">
        <div className="lep-page-inner" style={{ padding: "80px 20px" }}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontFamily: T.fontDisplay, fontSize: 20, fontWeight: 600, color: T.rust }}>Access Denied</div>
            <div style={{ fontFamily: T.font, fontSize: 15, color: T.ink2, marginTop: 8 }}>This area is for admins only.</div>
            <button onClick={() => navigate("/menu")} style={{
              marginTop: 24, height: 48, padding: "0 24px", border: "none",
              background: T.forest, color: "#fff", borderRadius: T.r.md,
              fontFamily: T.font, fontSize: 16, fontWeight: 600, cursor: "pointer",
            }}>Back to menu</button>
          </div>
        </div>
      </div>
    );
  }

  // ── Species filtering ──────────────────────────────────────────────────────
  const families = ["All", ...Array.from(new Set(butterflies.map(b => b.commonFamilyName || b.family).filter(Boolean)))];
  const visibleSpecies = butterflies.filter(b =>
    !search ||
    b.commonName?.toLowerCase().includes(search.toLowerCase()) ||
    b.scientificName?.toLowerCase().includes(search.toLowerCase())
  );
  const filteredSpecies = activeFamily === "All" ? visibleSpecies : visibleSpecies.filter(b => (b.commonFamilyName || b.family) === activeFamily);

  // ── User filtering ─────────────────────────────────────────────────────────
  const filteredUsers = users.filter(u =>
    !userSearch ||
    u.username?.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.firstName?.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.lastName?.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.email?.toLowerCase().includes(userSearch.toLowerCase())
  );

  // ── Shared styles ──────────────────────────────────────────────────────────
  const inputStyle = {
    width: "100%", height: 54, padding: "0 16px",
    background: T.card, border: `1px solid ${T.line}`, borderRadius: T.r.md,
    fontFamily: T.font, fontSize: 16, color: T.ink, outline: "none",
    letterSpacing: "-0.01em", marginBottom: 10, boxSizing: "border-box",
  };
  const labelStyle = {
    fontFamily: T.font, fontSize: 12, fontWeight: 600, color: T.ink2,
    letterSpacing: "0.04em", textTransform: "uppercase", marginBottom: 4, display: "block",
  };

  return (
    <div className="lep-page">
      {confirm && (
        <ConfirmModal
          message={confirm.message}
          confirmLabel={confirm.confirmLabel}
          danger={confirm.danger}
          onConfirm={confirm.onConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}

      <div className="lep-page-inner">
        {/* ── Header ── */}
        <div style={{
          display: "flex", alignItems: "center", padding: "54px 16px 12px", gap: 8,
          background: T.bg, position: "sticky", top: 0, zIndex: 10,
        }}>
          <button onClick={() => navigate("/menu")} style={{
            width: 40, height: 40, border: "none", background: "transparent",
            borderRadius: T.r.pill, display: "flex", alignItems: "center",
            justifyContent: "center", color: T.ink, cursor: "pointer",
          }}>
            <ArrowLeft />
          </button>
          <div style={{ flex: 1, textAlign: "center" }}>
            <div style={{ fontFamily: T.fontDisplay, fontSize: 17, fontWeight: 600, color: T.ink, letterSpacing: "-0.01em" }}>
              {tab === "species" ? "Species library" : "User management"}
            </div>
            <div style={{ fontFamily: T.font, fontSize: 12, color: T.ink2, marginTop: 1 }}>Admin</div>
          </div>
          {tab === "species" && (
            <>
              <button onClick={() => navigate("/analytics")} style={{
                height: 40, padding: "0 14px", borderRadius: T.r.md,
                border: `1px solid ${T.line}`, background: T.forest, color: "#fff",
                cursor: "pointer", fontFamily: T.font, fontSize: 13, fontWeight: 600, marginRight: 8,
              }}>📊 Analytics</button>
              <button onClick={() => setShowAdd(s => !s)} style={{
                width: 40, height: 40, border: "none", background: "transparent",
                borderRadius: T.r.pill, display: "flex", alignItems: "center",
                justifyContent: "center", color: T.ink, cursor: "pointer",
              }}>
                <PlusIcon />
              </button>
            </>
          )}
          {tab === "users" && (
            <button onClick={fetchUsers} style={{
              height: 40, padding: "0 14px", borderRadius: T.r.md,
              border: `1px solid ${T.line}`, background: "transparent", color: T.ink,
              cursor: "pointer", fontFamily: T.font, fontSize: 13, fontWeight: 600,
            }}>Refresh</button>
          )}
        </div>

        {/* ── Tab bar ── */}
        <div style={{ display: "flex", padding: "0 20px", gap: 8, marginBottom: 16 }}>
          {[
            { key: "species", label: "Species", icon: <span style={{ fontSize: 14 }}>🦋</span> },
            { key: "users",   label: "Users",   icon: <UserIcon /> },
          ].map(({ key, label, icon }) => (
            <button key={key} onClick={() => setTab(key)} style={{
              flex: 1, height: 44, display: "flex", alignItems: "center", justifyContent: "center",
              gap: 7, border: "none", cursor: "pointer", borderRadius: T.r.md,
              fontFamily: T.font, fontSize: 14, fontWeight: 600,
              background: tab === key ? T.ink : T.card,
              color: tab === key ? "#fff" : T.ink2,
              transition: "background 0.15s, color 0.15s",
            }}>
              {icon}{label}
            </button>
          ))}
        </div>

        <div style={{ padding: "0 20px", paddingBottom: 48 }}>

          {/* ════════════════════════════════════════════════════════════════ */}
          {/* SPECIES TAB                                                      */}
          {/* ════════════════════════════════════════════════════════════════ */}
          {tab === "species" && (
            <>
              {/* Add form */}
              {showAdd && (
                <div style={{ background: T.card, borderRadius: T.r.lg, border: `1px solid ${T.line}`, padding: 16, marginBottom: 16 }}>
                  <div style={{ fontFamily: T.font, fontSize: 14, fontWeight: 600, color: T.ink, marginBottom: 14 }}>Add species</div>

                  {[
                    { label: "Species", fields: [{ ph: "Common name", name: "commonName" }, { ph: "Scientific name", name: "scientificName" }] },
                    { label: "Family", fields: [{ ph: "Common family name", name: "commonFamilyName" }, { ph: "Scientific family name", name: "scientificFamilyName" }] },
                    { label: "Subfamily", fields: [{ ph: "Common subfamily name", name: "commonSubfamilyName" }, { ph: "Scientific subfamily name", name: "scientificSubfamilyName" }] },
                    { label: "States", fields: [{ ph: "e.g. IA, IL, TX", name: "states" }] },
                  ].map(({ label, fields }) => (
                    <div key={label} style={{ marginBottom: 14 }}>
                      <label style={labelStyle}>{label}</label>
                      {fields.map(f => (
                        <input key={f.name} style={inputStyle} placeholder={f.ph} name={f.name} value={form[f.name]} onChange={e => setForm({ ...form, [e.target.name]: e.target.value })} />
                      ))}
                    </div>
                  ))}

                  <div style={{ marginBottom: 14 }}>
                    <label style={labelStyle}>Description</label>
                    <textarea placeholder="Optional description" name="description" value={form.description}
                      onChange={e => setForm({ ...form, description: e.target.value })}
                      rows={3} style={{ ...inputStyle, height: "auto", padding: 16, resize: "vertical" }} />
                  </div>

                  <div style={{ display: "flex", gap: 10 }}>
                    <button onClick={() => setShowAdd(false)} style={{ flex: 1, height: 48, border: `1px solid ${T.lineStrong}`, background: T.card, color: T.ink, borderRadius: T.r.md, fontFamily: T.font, fontSize: 16, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
                    <button onClick={addButterfly} style={{ flex: 2, height: 48, border: "none", background: T.forest, color: "#fff", borderRadius: T.r.md, fontFamily: T.font, fontSize: 16, fontWeight: 600, cursor: "pointer" }}>Add butterfly</button>
                  </div>
                </div>
              )}

              {/* Search */}
              <div style={{ display: "flex", alignItems: "center", background: T.card, borderRadius: T.r.md, border: `1px solid ${T.line}`, height: 54, paddingLeft: 14, marginBottom: 14 }}>
                <span style={{ color: T.ink3, display: "flex", marginRight: 10 }}><SearchIcon /></span>
                <input type="text" value={search} onChange={e => { setSearch(e.target.value); setShowAll(false); }}
                  placeholder="Search species, family…"
                  style={{ flex: 1, height: "100%", border: "none", outline: "none", background: "transparent", fontFamily: T.font, fontSize: 17, color: T.ink, padding: "0 16px 0 0", letterSpacing: "-0.01em", minWidth: 0 }} />
              </div>

              {/* Family chips */}
              <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4, marginBottom: 18 }}>
                {families.slice(0, 6).map(fam => (
                  <button key={fam} onClick={() => { setActiveFamily(fam); setShowAll(false); }} style={{
                    flexShrink: 0, padding: "8px 14px", borderRadius: 9999,
                    background: activeFamily === fam ? T.ink : T.card,
                    color: activeFamily === fam ? "#fff" : T.ink,
                    fontFamily: T.font, fontSize: 13, fontWeight: 500,
                    border: `1px solid ${activeFamily === fam ? T.ink : T.line}`,
                    cursor: "pointer",
                  }}>{fam}</button>
                ))}
              </div>

              {/* Species list */}
              <div style={{ background: T.card, borderRadius: T.r.lg, border: `1px solid ${T.line}`, overflow: "hidden" }}>
                {filteredSpecies.length === 0 && (
                  <div style={{ padding: 24, textAlign: "center", fontFamily: T.font, fontSize: 14, color: T.ink2 }}>No species found.</div>
                )}
                {(showAll ? filteredSpecies : filteredSpecies.slice(0, PREVIEW_COUNT)).map((b, i) => (
                  <div key={b.id}>
                    <div onClick={() => setExpandedId(expandedId === b.id ? null : b.id)} style={{ display: "flex", alignItems: "center", padding: "14px 14px", borderTop: i === 0 ? "none" : `1px solid ${T.line}`, gap: 12, cursor: "pointer" }}>
                      <div style={{ width: 36, height: 36, borderRadius: 10, flexShrink: 0, background: familyColor(b.commonFamilyName || b.family || ""), opacity: 0.9 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontFamily: T.font, fontSize: 15, fontWeight: 600, color: T.ink, letterSpacing: "-0.01em" }}>{b.commonName}</div>
                        <div style={{ fontFamily: T.fontSerif, fontStyle: "italic", fontSize: 13, color: T.ink2, marginTop: 1 }}>{b.scientificName}</div>
                      </div>
                      <span style={{ color: T.ink3, display: "flex", transform: expandedId === b.id ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}><ChevronDown /></span>
                      <button onClick={e => { e.stopPropagation(); deleteButterfly(b.id, b.commonName); }} style={{ width: 36, height: 36, borderRadius: 10, border: `1px solid ${T.line}`, background: "transparent", color: T.rust, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <TrashIcon />
                      </button>
                    </div>
                    {expandedId === b.id && (
                      <div style={{ padding: "0 16px 16px 66px", borderTop: `1px solid ${T.line}`, background: T.bg }}>
                        {(b.commonFamilyName || b.scientificFamilyName) && (
                          <div style={{ marginTop: 10 }}>
                            <span style={labelStyle}>Family</span>
                            <div style={{ fontFamily: T.font, fontSize: 14, color: T.ink, marginTop: 2 }}>
                              {b.commonFamilyName}{b.scientificFamilyName && <span style={{ fontStyle: "italic", color: T.ink2 }}> · {b.scientificFamilyName}</span>}
                            </div>
                          </div>
                        )}
                        {(b.commonSubfamilyName || b.scientificSubfamilyName) && (
                          <div style={{ marginTop: 8 }}>
                            <span style={labelStyle}>Subfamily</span>
                            <div style={{ fontFamily: T.font, fontSize: 14, color: T.ink, marginTop: 2 }}>
                              {b.commonSubfamilyName}{b.scientificSubfamilyName && <span style={{ fontStyle: "italic", color: T.ink2 }}> · {b.scientificSubfamilyName}</span>}
                            </div>
                          </div>
                        )}
                        {b.states?.length > 0 && (
                          <div style={{ marginTop: 8 }}>
                            <span style={labelStyle}>States</span>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 4 }}>
                              {b.states.map(s => (
                                <span key={s} style={{ padding: "3px 8px", borderRadius: 6, background: T.line, fontFamily: T.font, fontSize: 12, color: T.ink, fontWeight: 500 }}>{s}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        {b.description && (
                          <div style={{ marginTop: 8 }}>
                            <span style={labelStyle}>Description</span>
                            <div style={{ fontFamily: T.font, fontSize: 14, color: T.ink2, marginTop: 2, lineHeight: 1.5 }}>{b.description}</div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {filteredSpecies.length > PREVIEW_COUNT && (
                <button onClick={() => setShowAll(s => !s)} style={{
                  width: "100%", marginTop: 10, height: 48, background: T.card,
                  border: `1px solid ${T.line}`, borderRadius: T.r.md, cursor: "pointer",
                  fontFamily: T.font, fontSize: 14, fontWeight: 600, color: T.ink,
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                }}>
                  <span style={{ display: "flex", transform: showAll ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}><ChevronDown /></span>
                  {showAll ? "Show less" : `Show all ${filteredSpecies.length} species`}
                </button>
              )}

              <div style={{ marginTop: 12, fontFamily: T.font, fontSize: 12, color: T.ink3, textAlign: "center" }}>
                {butterflies.length} species in library
              </div>
            </>
          )}

          {/* ════════════════════════════════════════════════════════════════ */}
          {/* USERS TAB                                                        */}
          {/* ════════════════════════════════════════════════════════════════ */}
          {tab === "users" && (
            <>
              {/* Search */}
              <div style={{ display: "flex", alignItems: "center", background: T.card, borderRadius: T.r.md, border: `1px solid ${T.line}`, height: 54, paddingLeft: 14, marginBottom: 14 }}>
                <span style={{ color: T.ink3, display: "flex", marginRight: 10 }}><SearchIcon /></span>
                <input type="text" value={userSearch} onChange={e => setUserSearch(e.target.value)}
                  placeholder="Search by name, username, email…"
                  style={{ flex: 1, height: "100%", border: "none", outline: "none", background: "transparent", fontFamily: T.font, fontSize: 17, color: T.ink, padding: "0 16px 0 0", letterSpacing: "-0.01em", minWidth: 0 }} />
              </div>

              {/* Loading / error */}
              {usersLoading && (
                <div style={{ textAlign: "center", padding: "40px 0", fontFamily: T.font, fontSize: 14, color: T.ink2 }}>
                  Loading users…
                </div>
              )}
              {!usersLoading && usersError && (
                <div style={{ background: T.card, border: `1px solid ${T.rust}`, borderRadius: T.r.md, padding: 16, fontFamily: T.font, fontSize: 14, color: T.rust, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span>{usersError}</span>
                  <button onClick={fetchUsers} style={{ padding: "6px 14px", border: `1px solid ${T.rust}`, borderRadius: T.r.md, background: "transparent", color: T.rust, fontFamily: T.font, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Retry</button>
                </div>
              )}

              {/* User list */}
              {!usersLoading && !usersError && (
                <>
                  <div style={{ background: T.card, borderRadius: T.r.lg, border: `1px solid ${T.line}`, overflow: "hidden" }}>
                    {filteredUsers.length === 0 && (
                      <div style={{ padding: 24, textAlign: "center", fontFamily: T.font, fontSize: 14, color: T.ink2 }}>No users found.</div>
                    )}
                    {filteredUsers.map((u, i) => {
                      const isSelf = u.id === userId;
                      const busy = busyIds.has(u.id);
                      const fullName = [u.firstName, u.lastName].filter(Boolean).join(" ") || null;
                      return (
                        <div key={u.id} style={{
                          display: "flex", alignItems: "center", padding: "14px 14px",
                          borderTop: i === 0 ? "none" : `1px solid ${T.line}`,
                          gap: 12, opacity: busy ? 0.6 : 1, transition: "opacity 0.2s",
                          background: isSelf ? `${T.forest}0d` : "transparent",
                        }}>
                          {/* Avatar */}
                          <div style={{
                            width: 38, height: 38, borderRadius: "50%", flexShrink: 0,
                            background: u.admin ? T.rust : T.forest,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            color: "#fff",
                          }}>
                            {u.admin ? <ShieldIcon /> : <UserIcon />}
                          </div>

                          {/* Info */}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                              <span style={{ fontFamily: T.font, fontSize: 15, fontWeight: 600, color: T.ink, letterSpacing: "-0.01em" }}>{u.username}</span>
                              {isSelf && <span style={{ fontFamily: T.font, fontSize: 11, fontWeight: 600, color: T.forest, background: `${T.forest}22`, padding: "2px 7px", borderRadius: 9999 }}>You</span>}
                              {u.admin && <span style={{ fontFamily: T.font, fontSize: 11, fontWeight: 600, color: T.rust, background: `${T.rust}18`, padding: "2px 7px", borderRadius: 9999 }}>Admin</span>}
                            </div>
                            {fullName && <div style={{ fontFamily: T.font, fontSize: 13, color: T.ink2, marginTop: 1 }}>{fullName}</div>}
                            <div style={{ fontFamily: T.font, fontSize: 12, color: T.ink3, marginTop: 1 }}>{u.email}</div>
                          </div>

                          {/* Actions */}
                          {!isSelf && (
                            <div style={{ display: "flex", flexDirection: "column", gap: 6, flexShrink: 0 }}>
                              <button
                                disabled={busy}
                                onClick={() => toggleAdmin(u)}
                                style={{
                                  height: 32, padding: "0 12px", borderRadius: T.r.md, cursor: busy ? "default" : "pointer",
                                  border: `1px solid ${u.admin ? T.lineStrong : T.rust}`,
                                  background: "transparent",
                                  color: u.admin ? T.ink2 : T.rust,
                                  fontFamily: T.font, fontSize: 12, fontWeight: 600,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {busy ? "…" : u.admin ? "Revoke admin" : "Make admin"}
                              </button>
                              <button
                                disabled={busy}
                                onClick={() => deleteUser(u)}
                                style={{
                                  height: 32, padding: "0 12px", borderRadius: T.r.md, cursor: busy ? "default" : "pointer",
                                  border: `1px solid ${T.line}`, background: "transparent", color: T.rust,
                                  fontFamily: T.font, fontSize: 12, fontWeight: 600,
                                  display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
                                }}
                              >
                                <TrashIcon />Delete
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <div style={{ marginTop: 12, fontFamily: T.font, fontSize: 12, color: T.ink3, textAlign: "center" }}>
                    {users.length} user{users.length !== 1 ? "s" : ""} · {users.filter(u => u.admin).length} admin{users.filter(u => u.admin).length !== 1 ? "s" : ""}
                  </div>
                </>
              )}
            </>
          )}

        </div>
      </div>
    </div>
  );
}